(function () {
  'use strict';

  class BloodyMaryBuilder {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-bmb-form]');
      this.currency = root.dataset.currency || 'USD';
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currency
      });
      this.bind();
      this.applyTier();
    }

    bind() {
      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[data-tier]')) this.applyTier();
        if (event.target.matches('[data-choice]')) this.handleChoice(event.target);
        if (event.target.matches('[data-addon-toggle]')) this.handleAddonToggle(event.target);
        this.updateTotal();
      });
      this.form.addEventListener('input', () => this.updateTotal());
      this.form.addEventListener('submit', (event) => this.submit(event));
    }

    get tier() {
      return this.form.querySelector('[data-tier]:checked');
    }

    get guests() {
      return BMB.integer(this.form.querySelector('[data-guests]').value);
    }

    tierRule(name) {
      return BMB.integer(this.tier.dataset[name]);
    }

    applyTier() {
      const minimum = this.tierRule('minimum');
      const guestInput = this.form.querySelector('[data-guests]');
      guestInput.min = minimum;
      if (BMB.integer(guestInput.value) < minimum) guestInput.value = minimum;

      ['mixes', 'rimmers', 'toppings'].forEach((group) => {
        const limit = this.tierRule(group);
        this.root.querySelectorAll(`[data-limit-label="${group}"]`).forEach((node) => {
          node.textContent = limit;
        });
        const checked = [...this.root.querySelectorAll(`[data-choice="${group}"]:checked`)];
        checked.slice(limit).forEach((input) => { input.checked = false; });
        this.syncChoiceAvailability(group);
      });

      this.root.querySelector('[data-guest-hint]').textContent = `Minimum ${minimum} guests for ${this.tier.dataset.title}.`;
      this.clearErrors();
      this.updateTotal();
    }

    handleChoice(input) {
      const group = input.dataset.choice;
      const limit = this.tierRule(group);
      const checked = this.root.querySelectorAll(`[data-choice="${group}"]:checked`);
      if (checked.length > limit) {
        input.checked = false;
        this.showFieldError(group, `You can select exactly ${limit} ${this.label(group)}.`);
      } else {
        this.showFieldError(group, '');
      }
      this.syncChoiceAvailability(group);
    }

    syncChoiceAvailability(group) {
      const limit = this.tierRule(group);
      const inputs = [...this.root.querySelectorAll(`[data-choice="${group}"]`)];
      const atLimit = inputs.filter((input) => input.checked).length >= limit;
      inputs.forEach((input) => {
        input.closest('.bmb__choice-card').classList.toggle('is-unavailable', atLimit && !input.checked);
      });
    }

    handleAddonToggle(toggle) {
      const addon = toggle.closest('[data-addon]');
      const mode = addon.dataset.mode;
      const manual = mode === 'manual' || mode === 'per_pound';
      addon.querySelector('[data-addon-quantity-wrap]').hidden = !(toggle.checked && manual);
      this.updateAddonDisplay(addon);
    }

    addonState(addon) {
      const selected = addon.querySelector('[data-addon-toggle]').checked;
      const manualQuantity = addon.querySelector('[data-addon-quantity]').value;
      const quantity = BMB.addonQuantity({
        mode: addon.dataset.mode,
        percentage: Number(addon.dataset.percentage),
        manualQuantity
      }, this.guests);
      return {
        selected,
        quantity,
        priceCents: BMB.integer(addon.dataset.price),
        variantId: BMB.integer(addon.dataset.variantId),
        name: addon.dataset.name,
        mode: addon.dataset.mode
      };
    }

    updateAddonDisplay(addon) {
      const state = this.addonState(addon);
      const output = addon.querySelector('[data-addon-calculated]');
      output.textContent = state.selected ? `${state.quantity} × ${this.formatMoney(state.priceCents)}` : '';
    }

    updateTotal() {
      const addons = [...this.root.querySelectorAll('[data-addon]')].map((addon) => {
        this.updateAddonDisplay(addon);
        return this.addonState(addon);
      });
      const total = BMB.grandTotal(this.tierRule('price'), this.guests, addons);
      this.root.querySelector('[data-total]').textContent = this.formatMoney(total);
    }

    formatMoney(cents) {
      return this.money.format(BMB.integer(cents) / 100);
    }

    selectedValues(group) {
      return [...this.root.querySelectorAll(`[data-choice="${group}"]:checked`)].map((input) => input.value);
    }

    label(group) {
      return group.charAt(0).toUpperCase() + group.slice(1);
    }

    validate() {
      this.clearErrors();
      const guestResult = BMB.validateGuestCount(this.guests, this.tierRule('minimum'));
      const errors = [];
      if (!guestResult.valid) errors.push(guestResult.message);

      ['mixes', 'rimmers', 'toppings'].forEach((group) => {
        const result = BMB.validateSelectionCount(
          this.label(group),
          this.selectedValues(group).length,
          this.tierRule(group)
        );
        if (!result.valid) {
          errors.push(result.message);
          this.showFieldError(group, result.message);
        }
      });

      if (errors.length) {
        this.showFormError(errors[0]);
        const firstError = this.root.querySelector('.bmb__field-error:not(:empty)');
        if (firstError) firstError.closest('.bmb__step').scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      return true;
    }

    cartLines() {
      const group = BMB.groupId(this.root.dataset.productHandle, Date.now(), Math.random());
      const properties = {
        '_Builder group': group,
        'Package': this.tier.dataset.title,
        'Guests': String(this.guests),
        'Mixes': this.selectedValues('mixes').join(', '),
        'Rimmers': this.selectedValues('rimmers').join(', '),
        'Toppings': this.selectedValues('toppings').join(', ')
      };
      const items = [{ id: BMB.integer(this.tier.value), quantity: this.guests, properties }];

      this.root.querySelectorAll('[data-addon]').forEach((addon) => {
        const state = this.addonState(addon);
        if (!state.selected) return;
        items.push({
          id: state.variantId,
          quantity: state.quantity,
          properties: {
            '_Builder group': group,
            '_Parent product': this.root.dataset.productHandle,
            'Add-on for': this.tier.dataset.title,
            'Guests': String(this.guests)
          }
        });
      });
      return items;
    }

    async submit(event) {
      event.preventDefault();
      if (!this.validate()) return;

      const button = this.root.querySelector('[data-submit]');
      BMB.setButtonLoading(button, true);

      try {
        const cartUi = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = cartUi && typeof cartUi.getSectionsToRender === 'function'
          ? cartUi.getSectionsToRender().map((section) => section.id)
          : [];
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: this.cartLines(),
            sections,
            sections_url: window.location.pathname
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'The package could not be added to cart.');

        BMB.renderCartOrRedirect(
          cartUi,
          result,
          `${window.Shopify?.routes?.root || '/'}cart`,
          window.location
        );
      } catch (error) {
        this.showFormError(error.message || 'Something went wrong. Please try again.');
      } finally {
        BMB.setButtonLoading(button, false);
      }
    }

    showFieldError(group, message) {
      const node = this.root.querySelector(`[data-error="${group}"]`);
      if (node) node.textContent = message;
    }

    showFormError(message) {
      const node = this.root.querySelector('[data-form-error]');
      node.textContent = message;
      node.hidden = !message;
    }

    clearErrors() {
      this.root.querySelectorAll('[data-error]').forEach((node) => { node.textContent = ''; });
      this.showFormError('');
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-bmb-builder]').forEach((root) => {
      if (!root.dataset.bmbReady) {
        root.dataset.bmbReady = 'true';
        new BloodyMaryBuilder(root);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
